/**
 * What each scene's load was like on THIS device, remembered across opens:
 * how long it took, and how badly the main thread stalled while it did.
 *
 * The time cuts the stand demo's loading animation and bar to length (the
 * loader reads the last measured time for the scene it is about to show).
 * The stalls are the number to read off in Setup when the loader looks
 * jerky: a stall is a stretch where the main thread ran nothing for over
 * 100 ms — an engine upload or decode — and during one nothing driven by
 * the page can move. Wrapped in try: an installed app or a partitioned
 * iframe may have no storage, and that must mean "no record", never a crash.
 */

const PREFIX = 'ollo.demo.load:';

export interface LoadRecord {
    /** Click to scene, ms. */
    ms: number;
    /** Main-thread stalls over STALL_MS during the load. */
    stalls: number;
    /** The longest one, ms. */
    worst: number;
    /** All of them added up, ms. */
    stalledMs: number;
    /** When each stall began (ms from the start of the load) and how long it
     *  was, the first ten — says WHICH step of the load freezes. */
    timeline?: [number, number][];
    /** When the scene appeared, and when it reached full detail (every tier
     *  applied, stream quiet), ms from the start. No `settle` = it had not
     *  got there when the record was written. */
    markMs?: { reveal?: number; settle?: number };
    /** Why the full-detail gate let go: 'stable' (the real thing), 'wedge'
     *  (nothing moved for 6 s), 'absolute-cap', or 'pending' — the gate was
     *  still waiting. The number to read on a device where the black after
     *  the loader's animation ran long (the work iPhone, 13/10/2026). */
    settle?: string;
    /** Loader video frames dropped, of how many were due. */
    video?: { dropped: number; total: number };
}

export function recordLoad(sceneUrl: string, rec: LoadRecord): void {
    if (!(rec.ms > 0)) return;
    try {
        window.localStorage.setItem(PREFIX + sceneUrl, JSON.stringify({
            ms: Math.round(rec.ms), stalls: rec.stalls, worst: Math.round(rec.worst), stalledMs: Math.round(rec.stalledMs),
            timeline: rec.timeline ?? [], markMs: rec.markMs ?? {}, settle: rec.settle, video: rec.video
        }));
    } catch { /* no storage here */ }
}

export function lastLoad(sceneUrl: string): LoadRecord | null {
    try {
        const raw = window.localStorage.getItem(PREFIX + sceneUrl);
        if (!raw) return null;
        // Earlier builds stored the bare number.
        if (/^\d+$/.test(raw)) return { ms: Number(raw), stalls: 0, worst: 0, stalledMs: 0 };
        const r = JSON.parse(raw) as Partial<LoadRecord>;
        return r.ms && r.ms > 0
            ? { ms: r.ms, stalls: r.stalls ?? 0, worst: r.worst ?? 0, stalledMs: r.stalledMs ?? 0, timeline: r.timeline ?? [], markMs: r.markMs ?? {}, settle: r.settle, video: r.video }
            : null;
    } catch {
        return null;
    }
}

export function expectedLoadMs(sceneUrl: string): number | null {
    return lastLoad(sceneUrl)?.ms ?? null;
}

/**
 * Main-thread stall meter: a 20 ms timer; any tick that arrives over STALL_MS
 * late is a stall of that length. Cheap, and it measures exactly what the
 * eye sees — the stretches in which the page could not run a frame.
 */
/** A stall is a tick this late: two dropped frames at 60 Hz — what the eye
 *  reads as a hitch (100 ms let a visibly jerky load through as "0 stalls"). */
export const STALL_MS = 40;

export function startStallMeter(): { stop(): Omit<LoadRecord, 'ms' | 'markMs' | 'video'> } {
    const TICK = 20;
    const THRESHOLD = STALL_MS;
    const t0 = performance.now();
    let stalls = 0, worst = 0, stalledMs = 0;
    const timeline: [number, number][] = [];
    let last = t0;
    const id = window.setInterval(() => {
        const now = performance.now();
        const gap = now - last - TICK;
        if (gap > THRESHOLD) {
            stalls++; stalledMs += gap; if (gap > worst) worst = gap;
            if (timeline.length < 10) timeline.push([Math.round(last - t0), Math.round(gap)]);
        }
        last = now;
    }, TICK);
    let stopped: Omit<LoadRecord, 'ms' | 'markMs' | 'video'> | null = null;
    return {
        stop() {
            if (stopped) return stopped;
            window.clearInterval(id);
            stopped = { stalls, worst, stalledMs, timeline };
            return stopped;
        }
    };
}
